/**
 * Death markers: a permanent white "X" stamped on the ground wherever a tank
 * (player, mercenary, or enemy) dies. Same pooled-InstancedMesh approach as the
 * tread trail — one draw call, fixed memory, a matrix written only when a mark is
 * dropped. Marks carry their own Y so they sit on whatever level the tank died on,
 * and the whole set is wiped between floors (the X's are "permanent" only for the
 * life of that floor).
 */

import * as THREE from "three";

const POOL = 256; // deaths are far rarer than tread drops; plenty for a long floor
const HIDDEN = new THREE.Matrix4().makeScale(0, 0, 0);
const _mat = new THREE.Matrix4();
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _axis = new THREE.Vector3(0, 1, 0);
const _one = new THREE.Vector3(1, 1, 1);

/** A white "X" decal texture — two thick diagonal strokes with a soft edge. */
function xTexture(): THREE.CanvasTexture {
  const cv = document.createElement("canvas");
  cv.width = 64; cv.height = 64;
  const ctx = cv.getContext("2d")!;
  ctx.clearRect(0, 0, 64, 64);
  ctx.lineCap = "round";
  // dark backing stroke first (so the X reads on light AND dark floors), white on top
  ctx.strokeStyle = "rgba(0,0,0,0.45)"; ctx.lineWidth = 15;
  ctx.beginPath(); ctx.moveTo(13, 13); ctx.lineTo(51, 51); ctx.moveTo(51, 13); ctx.lineTo(13, 51); ctx.stroke();
  ctx.strokeStyle = "rgba(255,255,255,0.95)"; ctx.lineWidth = 9;
  ctx.beginPath(); ctx.moveTo(13, 13); ctx.lineTo(51, 51); ctx.moveTo(51, 13); ctx.lineTo(13, 51); ctx.stroke();
  return new THREE.CanvasTexture(cv);
}

export class DeathMarks {
  private mesh: THREE.InstancedMesh;
  private next = 0;
  private dirty = false;

  constructor(scene: THREE.Scene) {
    const geom = new THREE.PlaneGeometry(26, 26); // a touch bigger than a tank — a clear grave marker
    geom.rotateX(-Math.PI / 2); // lay flat on the XZ plane
    const mat = new THREE.MeshBasicMaterial({
      map: xTexture(),
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      // sit just ABOVE tread marks (treads use -1) so the X is never buried by a trail
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
    });
    this.mesh = new THREE.InstancedMesh(geom, mat, POOL);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -7; // ground-decal ladder: just above treads (−8), below water (−6) + roads
    for (let i = 0; i < POOL; i++) this.mesh.setMatrixAt(i, HIDDEN);
    this.mesh.instanceMatrix.needsUpdate = true;
    scene.add(this.mesh);
  }

  /** Stamp a white X at (x,z) on level surface `floorY`. */
  drop(x: number, z: number, floorY: number): void {
    const i = this.next;
    this.next = (this.next + 1) % POOL;
    _pos.set(x, floorY + 0.7, z); // just over the tread layer (0.6)
    _quat.setFromAxisAngle(_axis, (i % 2) * 0.18); // tiny varied tilt so overlapping marks don't moiré
    this.mesh.setMatrixAt(i, _mat.compose(_pos, _quat, _one));
    this.dirty = true;
  }

  /** Flush GPU upload (once per frame). */
  update(): void {
    if (this.dirty) { this.mesh.instanceMatrix.needsUpdate = true; this.dirty = false; }
  }

  /** Wipe all marks (between floors). */
  clear(): void {
    for (let i = 0; i < POOL; i++) this.mesh.setMatrixAt(i, HIDDEN);
    this.mesh.instanceMatrix.needsUpdate = true;
    this.next = 0;
    this.dirty = false;
  }
}
